import React from "react";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { IconDotsVertical, IconCircleMinus, IconCheck, IconProgress, IconLoader } from "@tabler/icons-react";

const ListItemDemo: React.FC = () => {
  return (
    <div className="p-8 space-y-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-center mb-8">
        List Item Component Demo
      </h1>

      {/* CRUD Usage */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">CRUD Usage</h2>
        <div className="space-y-2 border rounded-lg p-4">
          <ListItem
            usage="crud"
            type="1-line"
            state="default"
            title="List Item Title"
            onAction={() => console.log("CRUD action clicked")}
          />
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            onAction={() => console.log("CRUD action clicked")}
          />
          <ListItem
            usage="crud"
            type="3-line"
            state="default"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            tertiaryText="Tertiary Text"
            onAction={() => console.log("CRUD action clicked")}
          />
        </div>
      </section>

      {/* Multi-Selection Usage */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Multi-Selection Usage
        </h2>
        <div className="space-y-2 border rounded-lg p-4">
          <ListItem
            usage="multi-selection"
            type="1-line"
            state="default"
            title="List Item Title"
            onAction={() => console.log("Multi-selection action clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="1-line"
            state="selected"
            title="List Item Title"
            onAction={() => console.log("Multi-selection action clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="2-line"
            state="default"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            onAction={() => console.log("Multi-selection action clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="2-line"
            state="selected"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            onAction={() => console.log("Multi-selection action clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="3-line"
            state="default"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            tertiaryText="Tertiary Text"
            onAction={() => console.log("Multi-selection action clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="3-line"
            state="selected"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
            tertiaryText="Tertiary Text"
            onAction={() => console.log("Multi-selection action clicked")}
          />
        </div>
      </section>

      {/* Upload States */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">Upload States</h2>
        <div className="space-y-2 border rounded-lg p-4">
          <ListItem
            usage="uploading"
            type="2-line"
            state="default"
            title="List Item Title"
            secondaryText="Secondary Text"
            emphasizedText="Emphasized Text"
          />
          <ListItem
            usage="processing-upload"
            type="2-line"
            state="default"
            title="List Item Title"
          />
          <ListItem
            usage="processed-upload"
            type="2-line"
            state="default"
            title="List Item Title"
          />
        </div>
      </section>

      {/* Interactive Examples */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Interactive Examples
        </h2>
        <div className="space-y-2 border rounded-lg p-4">
          {/* ListItem with multiple actions, icons, and conditional visibility */}
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            title="With Multiple Actions"
            secondaryText="Some actions hidden"
            emphasizedText="2.3 MB"
            actions={[
              {
                id: 'edit',
                label: 'Edit',
                icon: <IconDotsVertical size={18} />,
                onClick: () => alert('Edit clicked'),
                isVisible: true,
              },
              {
                id: 'delete',
                label: 'Delete',
                icon: <IconCircleMinus size={18} />,
                onClick: () => alert('Delete clicked'),
                isVisible: false, // Hidden
              },
              {
                id: 'share',
                label: 'Share',
                icon: <IconCheck size={18} />,
                onClick: () => alert('Share clicked'),
                isVisible: true,
              },
            ]}
          />
          {/* ListItem with custom actionsRenderer */}
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            title="Custom Actions Renderer"
            secondaryText="Custom rendering of actions"
            actions={[
              {
                id: 'progress',
                label: 'Progress',
                icon: <IconProgress size={18} />,
                onClick: () => alert('Progress clicked'),
                isVisible: true,
              },
              {
                id: 'loader',
                label: 'Loading',
                icon: <IconLoader size={18} className="animate-spin" />,
                onClick: () => alert('Loading...'),
                isVisible: true,
              },
            ]}
            actionsRenderer={actions => (
              <div className="flex gap-4">
                {actions.map(a => (
                  <button
                    key={a.id}
                    onClick={a.onClick}
                    style={{ border: '1px dashed #aaa', borderRadius: 8, padding: 4 }}
                  >
                    {a.icon} <span style={{ marginLeft: 4 }}>{a.label}</span>
                  </button>
                ))}
              </div>
            )}
          />
          {/* ListItem with error prop */}
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            title="With Error"
            secondaryText="Shows error message"
            error="This is an error message."
            onAction={() => alert('Error action')}
          />
          {/* Existing interactive examples */}
          <ListItem
            usage="crud"
            type="2-line"
            state="default"
            title="Document.pdf"
            secondaryText="Medical Report"
            emphasizedText="2.3 MB"
            onAction={() => alert("Document options clicked")}
          />
          <ListItem
            usage="multi-selection"
            type="3-line"
            state="selected"
            title="Patient Records"
            secondaryText="Last updated"
            emphasizedText="2 hours ago"
            tertiaryText="Dr. Smith, Cardiology"
            onAction={() => alert("Remove from selection")}
          />
          <ListItem
            usage="processing-upload"
            type="2-line"
            state="default"
            title="Lab_Results_2024.pdf"
          />
          <ListItem
            usage="processed-upload"
            type="2-line"
            state="default"
            title="Prescription_Jan2024.pdf"
          />
        </div>
      </section>
    </div>
  );
};

export default ListItemDemo;
